// NOTE: This component is largely copied from the SingleDatePicker component with styling changes
// Will eventually phase out SingleDatePicker and use this component instead
'use client'

import {
  formatDateColumnValue,
  LocaleCodeToDateFormatMap,
  LocaleCodeToPatternMap,
  LocaleCodeToTimeFormatMap,
  safeParseFormatOptions,
  TimeValue,
} from '@doss/common-all'
import { FormatPreset } from '@doss/prisma/types'
import dayjs, { Dayjs } from 'dayjs'
import { isNil } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'react-use'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Modal } from '~/components/ui/molecules/modal/Modal'
import { InputWrapper } from '../../base/input-wrapper'
import { BaseInputProps } from '../../base/types'
import { DatePickerContent } from './date-picker-content'
import { DatePickerClearButton, DatePickerInput } from './date-picker-controls'

export interface DatePickerProps extends Omit<BaseInputProps, 'placeholder'> {
  disabled?: boolean
  onBlur?: () => void
  onChange: (value?: Dayjs | 'now') => void
  onReset?: () => void
  showClearButton?: boolean
  showTodayButton?: boolean
  useModal?: boolean
  value?: Dayjs | 'now'
  withTime?: boolean
  formatPreset?: FormatPreset | null
  formatOptions?: unknown
  /**
   * Placeholder options:
   * - string: Custom placeholder text
   * - 'format': Show date format as placeholder (e.g. "MM/DD/YYYY")
   * - false/undefined: No placeholder
   */
  placeholder?: string | 'format' | false
}

export function DatePicker({
  disabled,
  wrapperClassName,
  errorText,
  helperText,
  label,
  required,
  showDirtyIndicator,
  tooltip,
  isBorderless = false,
  onBlur,
  onChange,
  onReset,
  showClearButton = false,
  showTodayButton = false,
  useModal = false,
  value,
  withTime = false,
  formatPreset,
  formatOptions,
  placeholder,
  readOnly,
}: DatePickerProps) {
  const { t } = useTypedTranslation()
  const { locale } = useGlobalContext()

  const dateFormat = LocaleCodeToDateFormatMap[locale]
  const timeFormat = LocaleCodeToTimeFormatMap[locale]

  // The expected regex pattern for date input validation
  const datePattern = LocaleCodeToPatternMap[locale]

  const customFormatOptions =
    formatPreset === FormatPreset.DATE_CUSTOM
      ? safeParseFormatOptions(FormatPreset.DATE_CUSTOM, formatOptions)
      : undefined
  const customPattern =
    customFormatOptions?.success && customFormatOptions.data.pattern !== ''
      ? customFormatOptions.data.pattern
      : null
  const inputDateFormat = customPattern ?? dateFormat

  const [isOpen, setIsOpen] = useState(false) // State to control popover visibility
  const [selected, setSelected] = useState<Dayjs | undefined>(() => {
    if (isNil(value)) {
      return undefined
    }
    return dayjs.isDayjs(value) ? value : undefined
  })

  const [inputValue, setInputValue] = useState(() => {
    if (isNil(value)) {
      return ''
    }
    if (value === 'now') {
      return 'Today'
    }
    if (!dayjs.isDayjs(value)) {
      return ''
    }
    return value.format(inputDateFormat)
  })

  const [timeValue, setTimeValue] = useState<TimeValue>(() => {
    if (dayjs.isDayjs(value)) {
      return {
        hour: value.hour(),
        minute: value.minute(),
      }
    }
    return { hour: 0, minute: 0 } // Default to 12:00 AM
  })

  // Initialize currentMonth to either the selected date or current date
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(() => {
    if (dayjs.isDayjs(value)) {
      return value
    }
    return dayjs()
  })

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isDynamicNow, setIsDynamicNow] = useState(value === 'now')

  // Sync internal state with external value
  useEffect(() => {
    if (isNil(value)) {
      // Reset all internal states when value is null/undefined
      setSelected(undefined)
      setTimeValue({ hour: 0, minute: 0 })
      setInputValue('')
      setIsDynamicNow(false)
      return
    }
    if (value === 'now') {
      setIsDynamicNow(true)
      setSelected(dayjs()) // Set selected date on calendar to today's date
      setInputValue(t('date_picker.today'))
      setTimeValue({
        hour: 0,
        minute: 0,
      })
    }
    if (dayjs.isDayjs(value)) {
      setSelected(value)
      setTimeValue({
        hour: value.hour(),
        minute: value.minute(),
      })
      setInputValue(value.format(inputDateFormat))
      setIsDynamicNow(false)
    }
  }, [value, inputDateFormat, t])

  const handleDateChange = (date?: Dayjs) => {
    if (!date) {
      setInputValue('')
      setIsDynamicNow(false)
      onChange(undefined)
      return
    }

    // Preserve existing time when changing date
    const newDate = date.hour(timeValue.hour).minute(timeValue.minute)

    setInputValue(newDate.format(inputDateFormat))
    setIsDynamicNow(false)
    onChange(newDate)

    // Only close the dialog if time is not required
    if (!withTime) {
      setIsOpen(false)
    }
  }

  const handleMonthChange = (newMonth: Date) => {
    setCurrentMonth(dayjs(newMonth))
  }

  useDebounce(
    () => {
      // Clear error if input is empty or "Today"
      if (!inputValue || inputValue === t('date_picker.today')) {
        setErrorMessage(null)
        return
      }

      const parsedDate = dayjs(inputValue, inputDateFormat, true)
      if (!parsedDate.isValid()) {
        setErrorMessage(
          t('date_picker.please_enter_a_valid_date_in_the_format', { format: inputDateFormat }),
        )
        return
      }

      setErrorMessage(null)
      const newDate = parsedDate.hour(timeValue.hour).minute(timeValue.minute)

      // Update currentMonth when date is typed in
      if (!parsedDate.isSame(currentMonth, 'month')) {
        setCurrentMonth(parsedDate)
      }

      setSelected(newDate)
      onChange(newDate)
    },
    300,
    [inputValue],
  )

  const handleInputChange = (value: string) => {
    setInputValue(value)
  }

  const handleTimeChange = useCallback(
    (type: 'hour' | 'minute', value: number) => {
      const newTimeValue = {
        ...timeValue,
        [type]: value,
      }
      setTimeValue(newTimeValue)

      if (selected) {
        let newDate
        if (type === 'hour') {
          newDate = selected.hour(value).minute(timeValue.minute)
        } else {
          newDate = selected.hour(timeValue.hour).minute(value)
        }
        setSelected(newDate)
        onChange(newDate)
      }
    },
    [selected, onChange, timeValue],
  )

  const handleTodayClick = () => {
    // Toggle is dynamic
    if (isDynamicNow) {
      // If previously was dynamic, on click set to today's date (not dynamic) and set is dynamic
      // to false
      setIsDynamicNow(false)
      onChange(dayjs())
    } else {
      // If previously was a static date, on click set to today's date and set is dynamic to true
      setSelected(dayjs())
      setIsDynamicNow(true)
      onChange('now')
    }
  }

  const handleResetClick = () => {
    if (onReset) {
      onReset()
    }
  }

  const handleDoneClick = () => {
    if (isDynamicNow) {
      onChange('now')
    } else {
      onChange(selected)
    }
    setIsOpen(false)
  }

  const getDisplayValue = useCallback(() => {
    if (isNil(value)) {
      // Handle placeholder based on prop value
      if (disabled || readOnly || !placeholder) return ''
      if (placeholder === 'format') return inputDateFormat
      return placeholder // Custom string placeholder
    }

    if (value === 'now') {
      return t('date_picker.today')
    }

    if (!dayjs.isDayjs(value)) {
      // Handle placeholder based on prop value
      if (disabled || readOnly || !placeholder) return ''
      if (placeholder === 'format') return inputDateFormat
      return placeholder // Custom string placeholder
    }

    const customFormat =
      formatPreset === FormatPreset.DATE_CUSTOM
        ? safeParseFormatOptions(FormatPreset.DATE_CUSTOM, formatOptions)
        : undefined
    if (customFormat?.success && customFormat.data.pattern !== '') {
      return formatDateColumnValue(
        value.toISOString(),
        { formatPreset, formatOptions, isDateTime: withTime },
        locale,
      )
    }

    // Format using the locale-specific time format
    const timeString = withTime ? value.format(timeFormat) : ''
    const dateString = value.format(dateFormat)

    return withTime ? `${dateString} ${timeString}` : dateString
  }, [
    value,
    dateFormat,
    inputDateFormat,
    timeFormat,
    withTime,
    formatPreset,
    formatOptions,
    locale,
    placeholder,
    disabled,
    readOnly,
    t,
  ])

  // Use event for special keys
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.stopPropagation() // Prevent debounce from firing
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      if (!errorMessage) {
        handleDoneClick()
      }
    }
  }

  const datePickerContentProps = {
    currentMonth,
    customPattern,
    dateFormat: inputDateFormat,
    datePattern,
    disabled,
    error: errorMessage,
    inputValue,
    isDynamicNow,
    locale,
    onDateChange: handleDateChange,
    onDoneClick: handleDoneClick,
    onInputChange: handleInputChange,
    onKeyDown: handleKeyDown,
    onMonthChange: handleMonthChange,
    onResetClick: onReset ? handleResetClick : undefined,
    onTimeChange: handleTimeChange,
    onTodayClick: handleTodayClick,
    selected: (() => {
      if (!selected) {
        return
      }
      if (withTime) {
        return selected.toDate()
      }
      return dayjs().year(selected.year()).month(selected.month()).date(selected.date()).toDate()
    })(),
    showTodayButton,
    value,
    withTime,
  }

  // If useModal is true, render editor as a full screen modal
  if (useModal) {
    return (
      <>
        <InputWrapper
          wrapperClassName={wrapperClassName}
          errorText={errorText}
          helperText={helperText}
          label={label}
          required={required}
          showDirtyIndicator={showDirtyIndicator}
          tooltip={tooltip}
        >
          <div className="group/date-picker relative">
            <DatePickerInput
              disabled={disabled}
              value={value}
              formatDateDisplay={getDisplayValue}
              isBorderless={isBorderless}
              onClick={() => setIsOpen(true)}
              isOpen={isOpen}
              readOnly={readOnly}
            />
            {showClearButton && !disabled && value && <DatePickerClearButton onChange={onChange} />}
          </div>
        </InputWrapper>
        <Modal
          onClose={() => {
            setIsOpen(false)
            onBlur?.()
          }}
          open={isOpen}
        >
          <DatePickerContent {...datePickerContentProps} />
        </Modal>
      </>
    )
  }

  // Otherwise, render editor as a popover
  return (
    <InputWrapper
      wrapperClassName={wrapperClassName}
      errorText={errorText}
      helperText={helperText}
      label={label}
      required={required}
      showDirtyIndicator={showDirtyIndicator}
      tooltip={tooltip}
    >
      <Popover
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) {
            onBlur?.()
          }
        }}
        open={isOpen}
      >
        <div className="group/date-picker relative">
          <PopoverTrigger asChild>
            <DatePickerInput
              disabled={disabled}
              value={value}
              formatDateDisplay={getDisplayValue}
              isBorderless={isBorderless}
              onClick={() => setIsOpen(true)}
              isOpen={isOpen}
              readOnly={readOnly}
            />
          </PopoverTrigger>
          {showClearButton && !disabled && value && <DatePickerClearButton onChange={onChange} />}
        </div>
        <PopoverContent align="start" className="z-[1301] w-auto p-0">
          <DatePickerContent {...datePickerContentProps} />
        </PopoverContent>
      </Popover>
    </InputWrapper>
  )
}
